/**
 * Fase 2 Sub-release 2.4b — Pagina Admin/Agente "Offerte in Lavorazione".
 *
 * Mostra i preventivi dei clienti che hanno messo il Visto Verde (con o senza
 * conferma documenti), con countdown 48h e pulsante "Ripristina Sconto" quando
 * il timer è scaduto.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Clock, CheckCircle2, RefreshCcw, Car, Mail, Phone, Loader2, Handshake, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCountdown(vistoVerdeAt) {
  if (!vistoVerdeAt) return '';
  try {
    const start = new Date(vistoVerdeAt);
    const end = new Date(start.getTime() + 48 * 3600 * 1000);
    const diff = end.getTime() - Date.now();
    if (diff <= 0) return 'Scaduto';
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h ${m}m`;
  } catch { return ''; }
}

function fmtEuro(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `€${Math.round(v).toLocaleString('it-IT')}`;
}

export default function AdminOfferteInLavorazionePage() {
  const { getAuthHeader, activeConvenzione } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tick, setTick] = useState(0);  // per refresh countdown

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = activeConvenzione?.id ? { convenzione_id: activeConvenzione.id } : {};
      const res = await axios.get(`${API}/admin/offerte-in-lavorazione`, { headers: getAuthHeader(), params });
      setRows(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel caricamento offerte in lavorazione');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, activeConvenzione?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRipristina = async (offerId) => {
    if (!window.confirm('Vuoi ripristinare lo sconto convenzione? Il timer riparte da 48h fresche.')) return;
    setBusyId(offerId);
    try {
      await axios.post(`${API}/admin/preventivi/${offerId}/ripristina-sconto`, {}, { headers: getAuthHeader() });
      toast.success('Sconto convenzione ripristinato. Il cliente ha di nuovo 48h.');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel ripristino sconto');
    } finally {
      setBusyId(null);
    }
  };

  // Segmenta: in lavorazione (docs confermati) prima, poi visto verde attivo, poi scaduti
  const inLavorazione = rows.filter((r) => r.in_lavorazione);
  const attiveNonConfermate = rows.filter((r) => !r.in_lavorazione && !r.visto_verde_scaduto);
  const scadute = rows.filter((r) => !r.in_lavorazione && r.visto_verde_scaduto);

  return (
    <div className="space-y-6" data-testid="offerte-in-lavorazione-page">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Offerte in Lavorazione
        </h1>
        <p className="text-slate-500 mt-1">
          Preventivi con Visto Verde attivo. Segui i timer 48h e sblocca lo sconto manualmente quando necessario.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-500">Nessuna offerta in lavorazione al momento.</CardContent></Card>
      ) : (
        <>
          <Section
            title="🟢 Pratiche in lavorazione (documenti confermati)"
            color="blue"
            rows={inLavorazione}
            busyId={busyId}
            onRipristina={handleRipristina}
            tick={tick}
          />
          <Section
            title="⏳ Visto Verde attivo (in attesa documenti)"
            color="emerald"
            rows={attiveNonConfermate}
            busyId={busyId}
            onRipristina={handleRipristina}
            tick={tick}
          />
          <Section
            title="⚠️ Timer scaduto (richiede sblocco manuale)"
            color="amber"
            rows={scadute}
            busyId={busyId}
            onRipristina={handleRipristina}
            tick={tick}
          />
        </>
      )}
    </div>
  );
}

const COLORS = {
  blue: { title: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', badge: 'bg-blue-100 text-blue-800 border-blue-300' },
  emerald: { title: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  amber: { title: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-800 border-amber-300' },
};

function Section({ title, color = 'emerald', rows, busyId, onRipristina, tick }) {
  const c = COLORS[color] || COLORS.emerald;
  if (!rows || rows.length === 0) return null;
  return (
    <Card data-testid={`section-${color}`}>
      <CardHeader>
        <CardTitle className={`text-base flex items-center gap-2 ${c.title}`}>
          {title}
          <Badge variant="outline" className="ml-auto">{rows.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((r) => (
          <OfferRow key={r.id} r={r} c={c} busyId={busyId} onRipristina={onRipristina} tick={tick} />
        ))}
      </CardContent>
    </Card>
  );
}

function OfferRow({ r, c, busyId, onRipristina, tick }) {
  const vi = r.vehicle_info || {};
  const veicolo = [vi.marca, vi.modello, vi.versione].filter(Boolean).join(' ') || 'Veicolo';
  const countdown = formatCountdown(r.visto_verde_at);
  const isScaduto = r.visto_verde_scaduto;
  const inLav = r.in_lavorazione;
  // Refresh visivo del countdown
  void tick;
  return (
    <div className={`rounded-xl border-2 p-4 ${c.bg}`} data-testid={`offer-row-${r.id}`}>
      <div className="flex flex-wrap gap-4 items-start">
        {/* Cliente */}
        <div className="flex-1 min-w-[220px]">
          <p className="text-xs uppercase text-slate-500">Cliente</p>
          <p className="text-base font-bold text-slate-800">{r.cliente_nome || '—'}</p>
          <div className="text-xs text-slate-600 flex flex-wrap gap-3 mt-1">
            {r.cliente_email && (
              <a href={`mailto:${r.cliente_email}`} className="flex items-center gap-1 hover:text-emerald-700">
                <Mail className="w-3 h-3" /> {r.cliente_email}
              </a>
            )}
            {r.cliente_telefono && (
              <a href={`tel:${r.cliente_telefono}`} className="flex items-center gap-1 hover:text-emerald-700">
                <Phone className="w-3 h-3" /> {r.cliente_telefono}
              </a>
            )}
          </div>
          {r.convenzione_nome && (
            <Badge className="mt-1.5 bg-white text-slate-700 border-slate-300 flex items-center gap-1 w-fit">
              <Handshake className="w-3 h-3" /> {r.convenzione_nome}
            </Badge>
          )}
        </div>

        {/* Veicolo */}
        <div className="flex-1 min-w-[200px]">
          <p className="text-xs uppercase text-slate-500">Veicolo</p>
          <p className="text-base font-semibold text-slate-800 flex items-center gap-1">
            <Car className="w-4 h-4 text-slate-500" /> {veicolo}
          </p>
          <p className="text-xs text-slate-600 mt-1">
            {r.durata_mesi || '—'} mesi · {(r.km_annui || 0).toLocaleString('it-IT')} km/anno · {fmtEuro(r.canone_finale || r.canone_base)}/mese
          </p>
        </div>

        {/* Consulente */}
        <div className="min-w-[160px]">
          <p className="text-xs uppercase text-slate-500">Consulente</p>
          <p className="text-sm font-semibold text-slate-800">{r.consulente_nome || 'Da assegnare'}</p>
          {r.consulente_email && (
            <p className="text-[11px] text-slate-500 truncate">{r.consulente_email}</p>
          )}
        </div>

        {/* Status + Azioni */}
        <div className="flex flex-col items-end gap-2 min-w-[180px]">
          {inLav ? (
            <Badge className="bg-blue-100 text-blue-800 border border-blue-300 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Documenti confermati
            </Badge>
          ) : isScaduto ? (
            <Badge className="bg-red-100 text-red-800 border border-red-300 flex items-center gap-1">
              ⚠️ Timer scaduto
            </Badge>
          ) : (
            <Badge className={`${c.badge} border flex items-center gap-1`}>
              <Clock className="w-3.5 h-3.5" /> {countdown || '48h'} restanti
            </Badge>
          )}

          {!inLav && (
            <Button
              size="sm"
              variant={isScaduto ? 'default' : 'outline'}
              disabled={busyId === r.id}
              onClick={() => onRipristina(r.id)}
              className={isScaduto ? 'bg-emerald-600 hover:bg-emerald-700 gap-1' : 'gap-1'}
              data-testid={`btn-ripristina-${r.id}`}
            >
              {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              Ripristina Sconto
            </Button>
          )}

          {inLav && r.cliente_email && (
            <a
              href={`mailto:${r.cliente_email}`}
              className="text-xs text-emerald-700 hover:underline flex items-center gap-1"
              data-testid={`link-mail-${r.id}`}
            >
              <Send className="w-3 h-3" /> Scrivi al cliente
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
